
import React from 'react';
import { Auth } from 'aws-amplify';
import Lottie from 'react-lottie';
import { default as animationData } from '../../assets/loader.json';
import { UserContext } from '../../components/UserContext';
import { css, cx } from '@emotion/css';
import Select from 'react-select';

// ─── STYLES ─────────────────────────────────────────────────────────────────

const container = css`
  max-width: 800px;
  margin: 60px auto;
  padding: 40px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 16px 50px rgba(0, 0, 0, 0.08);
  font-family: 'Poppins', sans-serif;
`;

const headerStyle = css`
  text-align: center;
  margin-bottom: 40px;

  h2 {
    font-size: 32px;
    font-weight: 800;
    color: #1a1a1a;
    margin: 0;
  }

  p {
    color: #888;
    font-size: 15px;
    margin: 8px 0 0;
  }
`;

const currentAvatarWrap = css`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
  position: relative;

  .img-box {
    width: 140px;
    height: 140px;
    border-radius: 50%;
    padding: 6px;
    background: linear-gradient(135deg, #ff7e14 0%, #e05500 100%);
    box-shadow: 0 8px 24px rgba(241, 102, 2, 0.3);

    img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
      border: 4px solid #fff;
    }
  }
`;

const gridLabel = css`
  font-size: 14px;
  font-weight: 700;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
  text-align: left;
`;

const avatarGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
  gap: 16px;
  margin-bottom: 40px;
  padding: 20px;
  background: #fdfdfd;
  border: 1px solid #eaeaea;
  border-radius: 20px;
`;

const avatarItem = css`
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid transparent;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 8px 20px rgba(0,0,0,0.1);
  }

  &.active {
    border-color: #f16602;
    transform: scale(1.05);
    box-shadow: 0 8px 20px rgba(241, 102, 2, 0.3);
  }
`;

const formSection = css`
  max-width: 400px;
  margin: 0 auto;
`;

const saveBtn = css`
  width: 100%;
  height: 52px;
  background: linear-gradient(135deg, #ff7e14 0%, #e05500 100%);
  color: #fff;
  border: none;
  border-radius: 14px;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 24px;
  transition: all 0.2s;
  box-shadow: 0 6px 20px rgba(241, 102, 2, 0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(241, 102, 2, 0.45);
  }

  &:disabled {
    background: #ccc;
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
`;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    height: '52px',
    borderRadius: '12px',
    border: state.isFocused ? '1.5px solid #f16602' : '1.5px solid #eaeaea',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(241,102,2,0.1)' : 'none',
    background: state.isFocused ? '#fff' : '#fafafa',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '15px',
    cursor: 'pointer',
    '&:hover': { borderColor: '#f16602' },
  }),
  option: (base, state) => ({
    ...base,
    fontFamily: 'Poppins, sans-serif',
    fontSize: '14px',
    background: state.isSelected ? '#f16602' : state.isFocused ? 'rgba(241,102,2,0.08)' : '#fff',
    color: state.isSelected ? '#fff' : '#333',
    cursor: 'pointer',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '14px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
    overflow: 'hidden',
  }),
};

// ─── COMPONENT ──────────────────────────────────────────────────────────────

class Settings extends React.Component {
  static contextType = UserContext;
  constructor() {
    super();
    this.state = {
      status: 'Save details',
      jwt: '',
      user: {},
      profilepicset: [
        '/avatar/54b19ada-d53e-4ee9-8882-9dfed1bf1396.jpg',
        '/avatar/download.jpeg',
        '/avatar/download.png',
        '/avatar/download2.jpeg',
        '/avatar/download3.jpeg',
        '/avatar/e67eb556-f125-4e24-95ad-8aff21b9926a.jpg'
      ],
      profilepic: '',
      level: 5,
      isSaving: false
    };

    this.getToken();
  }

  setLevel(level) {
    this.setState({ level: Number(level) });
  }

  componentWillUnmount() {
    const { user } = this.context;
    // Set fallback if needed on unmount
    this.setState({
      profilepic: user.profilepic && (user.profilepic.startsWith('http') || user.profilepic.startsWith('/avatar'))
        ? user.profilepic 
        : `../assets/${user.profilepic}.jpg`,
    });
  }

  updateuserDetails = async (userObj, jwt) => {
    this.setState({ status: 'Saving...', isSaving: true });

    let updatedUser = { ...userObj };
    updatedUser['grade'] = this.state.level;
    
    // Determine how to save profilepic
    if (this.state.profilepic.startsWith('http') || this.state.profilepic.startsWith('/avatar')) {
      updatedUser['profilepic'] = this.state.profilepic;
    } else if (this.state.profilepic.includes('../assets/')) {
      updatedUser['profilepic'] = this.state.profilepic.substring(10).slice(0, -4);
    } else {
      updatedUser['profilepic'] = this.state.profilepic;
    }

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: jwt },
      body: JSON.stringify(updatedUser),
    };

    try {
      const response = await fetch(`https://api.quizwizard.net/quiz/updateuser`, requestOptions);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update user details');
      }

      const { setUser } = this.context;
      setUser((prevUser) => ({
        ...prevUser,
        grade: this.state.level,
        profilepic: updatedUser['profilepic'],
      }));

      this.setState({ status: 'Saved Successfully!', isSaving: false });
      
      setTimeout(() => {
        this.setState({ status: 'Save details' });
      }, 3000);

    } catch (error) {
      console.error('Error updating user details:', error);
      this.setState({ status: 'Save failed', isSaving: false });
    }
  };

  getuserDetails = async (username, jwt) => {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: jwt },
      body: JSON.stringify({ username: username }),
    };

    const response = await fetch(`https://api.quizwizard.net/quiz/getuser`, requestOptions);
    const data = await response.json();

    let loadedPic = data['profilepic'];
    if (loadedPic && !loadedPic.startsWith('http') && !loadedPic.startsWith('/avatar')) {
      loadedPic = '../assets/' + loadedPic + '.jpg';
    }

    this.setState({
      profilepic: loadedPic || '',
      level: data['grade'],
      user: data,
      jwt: jwt,
    });
  };

  getToken = async () => {
    try {
      const res = await Auth.currentSession();
      const accessToken = res.getAccessToken();
      const jwt = accessToken.getJwtToken();
      const payload = accessToken.decodePayload();

      const user = await Auth.currentAuthenticatedUser();

      if (accessToken.payload.username.includes("Google_")) {
        this.getuserDetails(user.attributes['email'], jwt);
      } else {
        this.getuserDetails(payload.username, jwt);
      }

      document.title = 'Settings';
    } catch (e) {
      console.log(e);
    }
  };

  changeProfilePic(url) {
    this.setState({ profilepic: url });

    const { setUser } = this.context;
    setUser((prevUser) => ({
      ...prevUser,
      profilepic: url.startsWith('http') || url.startsWith('/avatar') ? url : url.substring(10).slice(0, -4),
    }));
  }

  render() {
    const leveloptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

    return (
      <div className={container}>
        <div className={headerStyle}>
          <h2>Profile Settings</h2>
          <p>Customize your avatar and grade level.</p>
        </div>

        <div className={currentAvatarWrap}>
          <div className="img-box">
            {this.state.profilepic === '' ? (
              <Lottie 
                options={{ loop: true, autoplay: true, animationData }} 
                height={128} 
                width={128} 
              />
            ) : (
              <img src={this.state.profilepic} alt="Current Profile" />
            )}
          </div>
        </div>

        <div className={gridLabel}>Select an Avatar</div>
        <div className={avatarGrid}>
          {this.state.profilepicset.map((item, index) => (
            <div
              key={index}
              className={cx(avatarItem, this.state.profilepic === item && 'active')}
              onClick={() => this.changeProfilePic(item)}
            >
              <img src={item} alt={`Avatar ${index}`} />
            </div>
          ))}
        </div>

        <div className={formSection}>
          <div className={gridLabel}>Your Grade</div>
          <Select
            styles={selectStyles}
            classNamePrefix="select"
            name="level"
            options={leveloptions.map((times) => ({
              value: times,
              label: `Grade ${times}`,
            }))}
            onChange={(e) => this.setLevel(e.value)}
            value={{ value: this.state.level, label: `Grade ${this.state.level}` }}
          />

          <button
            type="button"
            className={saveBtn}
            disabled={this.state.isSaving}
            onClick={() => this.updateuserDetails(this.state.user, this.state.jwt)}
          >
            {this.state.status}
          </button>
        </div>
      </div>
    );
  }
}

export default Settings;